import { FieldRepository } from '@aipb/gulliver/castor/data-access';
import { CastorFieldSelect, CastorType, CoreFieldInfoLang } from '@aipb/gulliver/castor/model';

import { Component, forwardRef, Input, ViewChild } from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  UntypedFormControl
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { TranslocoModule } from '@jsverse/transloco';
import sortBy from 'lodash-es/sortBy';
import { MatIconModule } from '@angular/material/icon';

export type FieldType = 'search' | 'sort';

@Component({
  selector: 'castor-field-select',
  templateUrl: './field-select.component.html',
  styleUrls: ['./field-select.component.scss'],
  standalone: true,
  imports: [
    TranslocoModule,
    FormsModule,
    ReactiveFormsModule,
    MatMenuModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FieldSelectComponent),
      multi: true
    }
  ]
})
export class FieldSelectComponent implements ControlValueAccessor {
  control = new UntypedFormControl();

  @Input() coreId: string;
  @Input() label: string;
  @Input() fieldType: FieldType;
  @Input() castorFieldSelect: CastorFieldSelect = null;
  @Input() hint: string;
  @Input() htmlId: string;

  associatedFields: CoreFieldInfoLang[] = [];

  @ViewChild(MatMenuTrigger) trigger: MatMenuTrigger;

  objectKeys = Object.keys;

  constructor(protected fieldRepository: FieldRepository) {}

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onTouched: () => void = () => {};

  onFieldTypeChange(evt: MouseEvent): void {
    if (this.fieldType !== 'search') {
      return;
    }

    const field = this.control.value;

    if (!field) {
      return;
    }

    if (field.associatedFieldIds.length === 0) {
      return;
    }

    this.associatedFields = [];

    field.associatedFieldIds.forEach((af) => {
      const f = this.fieldRepository.getSearchField(this.coreId, af);

      if (f) {
        this.associatedFields.push(f);
      }
    });

    this.trigger.openMenu();
    evt.stopPropagation();
  }

  onFieldTypeActivate(field: CoreFieldInfoLang): void {
    this.control.setValue(field);
  }

  getActiveFieldType(): CastorType {
    let field = null;

    const value = this.control.value ? this.control.value.fieldId : '';
    switch (this.fieldType) {
      case 'search':
        field = this.fieldRepository.getSearchField(this.coreId, value);
        break;
      case 'sort':
        field = this.fieldRepository.getSortField(this.coreId, value);
        break;
    }

    return field ? field.castorType : '';
  }

  getActiveFieldIcon(): string {
    const fieldType = this.getActiveFieldType();

    switch (fieldType) {
      case 'KEYWORD':
      case 'STRING':
        return 'format_quote';
      case 'TEXT':
        return 'description';
      case 'DATE':
      case 'DATE_RANGE':
        return 'calendar_today';
      case 'SPATIAL':
        return 'map';
      case 'NUMBER':
      case 'NUMBER_RANGE':
        return 'equal';
    }

    return 'block';
  }

  getFields(): CoreFieldInfoLang[] {
    switch (this.fieldType) {
      case 'search':
        return this.fieldRepository.getSearchFields(this.coreId);
      case 'sort':
        return this.fieldRepository.getSortFields(this.coreId);
    }

    return [];
  }

  hasGroups(): boolean {
    return !!this.castorFieldSelect;
  }

  hasGroupFields(group: string) {
    return group in this.getGroupFields();
  }

  getGroupFields(): {
    other: CoreFieldInfoLang[];
    [key: string]: CoreFieldInfoLang[];
  } {
    if (!this.castorFieldSelect) {
      return {
        other: []
      };
    }
    const groups: {
      other: CoreFieldInfoLang[];
      [key: string]: CoreFieldInfoLang[];
    } = {
      other: []
    };

    const searchFields = this.fieldRepository.getSearchFields(this.coreId);

    searchFields.forEach((f) => {
      if (f.fieldId in this.castorFieldSelect.fields) {
        const g = this.castorFieldSelect.fields[f.fieldId];

        if (!(g.group in groups)) {
          groups[g.group] = [];
        }

        groups[g.group].push(f);
      } else {
        groups.other.push(f);
      }
    });

    for (const key of Object.keys(groups)) {
      groups[key] = sortBy(groups[key], (e) => {
        return this.castorFieldSelect.fields[e.fieldId]?.order;
      });
    }

    return groups;
  }

  getGroupsOrder(): string[] {
    if (!this.castorFieldSelect) {
      return [];
    }
    return this.castorFieldSelect.groupsOrder;
  }

  writeValue(val: any): void {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    val && this.control.setValue(val, { emitEvent: false });
  }

  registerOnChange(fn: any): void {
    this.control.valueChanges.subscribe(fn);
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.control.disable() : this.control.enable();
  }

  compareFn(c1: CoreFieldInfoLang, c2: CoreFieldInfoLang): boolean {
    return c1 && c2 ? c1.fieldId === c2.fieldId : c1 === c2;
  }
}
